import type { RepositoryStore, RepositorySnapshot } from '@trove/core';
import type { DeviceKeys } from '@trove/crypto';
import type { VaultMode } from '@trove/contracts';

export interface StoredWorkspace {
  userId: string;
  repositoryId: string;
  collectionId: string;
  snapshot: RepositorySnapshot;
  /** When set, `snapshot` has local queued edits not yet committed/synced. */
  pendingBaseline?: RepositorySnapshot;
  /** Empty for open vaults (no E2E root key). */
  collectionKey: number[];
  deviceKeys: {
    encryptionPrivateKey: number[];
    encryptionPublicKey: number[];
    signingPrivateKey: number[];
    signingPublicKey: number[];
  };
  recipientEmail?: string;
  demoInstanceId?: string;
  shareCollections: Record<
    string,
    { collectionId: string; key: number[]; head: string; version: number; shareId?: string }
  >;
  /** Local access: own vault vs accepted folder share. */
  mode: 'owner' | 'shared';
  sharedLabel?: string;
  vaultMode: VaultMode;
  label: string;
  icon: string;
  slug?: string | null;
  deviceId?: string;
}

const DB_NAME = 'trove-local-v2';
const WORKSPACES = 'workspaces';
const META = 'meta';

const openDatabase = () =>
  new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(WORKSPACES)) db.createObjectStore(WORKSPACES);
      if (!db.objectStoreNames.contains(META)) db.createObjectStore(META);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const workspaceKey = (userId: string, repositoryId: string) => `${userId}:${repositoryId}`;

const normalizeWorkspace = (raw: StoredWorkspace): StoredWorkspace => ({
  ...raw,
  mode: raw.mode ?? 'owner',
  vaultMode: raw.vaultMode ?? 'locked',
  label: raw.label || 'Personal',
  icon: raw.icon || (raw.vaultMode === 'open' ? 'users' : 'lock'),
  collectionKey: raw.collectionKey ?? [],
});

export async function loadActiveRepositoryId(userId: string): Promise<string | null> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const request = db.transaction(META).objectStore(META).get(`active:${userId}`);
    request.onsuccess = () => resolve((request.result as string | undefined) ?? null);
    request.onerror = () => reject(request.error);
  });
}

export async function setActiveRepositoryId(userId: string, repositoryId: string): Promise<void> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const request = db
      .transaction(META, 'readwrite')
      .objectStore(META)
      .put(repositoryId, `active:${userId}`);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function listWorkspaces(userId: string): Promise<StoredWorkspace[]> {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const request = db.transaction(WORKSPACES).objectStore(WORKSPACES).getAll();
    request.onsuccess = () => {
      const rows = (request.result as StoredWorkspace[]).filter((row) => row.userId === userId);
      resolve(rows.map(normalizeWorkspace));
    };
    request.onerror = () => reject(request.error);
  });
}

export async function loadWorkspace(
  userId: string,
  repositoryId?: string | null,
): Promise<StoredWorkspace | null> {
  const db = await openDatabase();
  const activeId = repositoryId ?? (await loadActiveRepositoryId(userId));
  if (activeId) {
    const keyed = await new Promise<StoredWorkspace | null>((resolve, reject) => {
      const request = db
        .transaction(WORKSPACES)
        .objectStore(WORKSPACES)
        .get(workspaceKey(userId, activeId));
      request.onsuccess = () =>
        resolve(request.result ? normalizeWorkspace(request.result as StoredWorkspace) : null);
      request.onerror = () => reject(request.error);
    });
    if (keyed) return keyed;
  }

  // Legacy v1: single workspace keyed by userId only.
  try {
    const legacyDb = await new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open('trove-local-v1', 1);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    const legacy = await new Promise<StoredWorkspace | null>((resolve, reject) => {
      const request = legacyDb.transaction('workspaces').objectStore('workspaces').get(userId);
      request.onsuccess = () => {
        const row = request.result as StoredWorkspace | undefined;
        resolve(row ? normalizeWorkspace(row) : null);
      };
      request.onerror = () => reject(request.error);
    });
    if (legacy) {
      await saveWorkspace(legacy);
      await setActiveRepositoryId(userId, legacy.repositoryId);
      return legacy;
    }
  } catch {
    // No legacy DB.
  }

  const all = await listWorkspaces(userId);
  if (all[0]) {
    await setActiveRepositoryId(userId, all[0].repositoryId);
    return all[0];
  }
  return null;
}

export async function saveWorkspace(workspace: StoredWorkspace): Promise<void> {
  const normalized = normalizeWorkspace(workspace);
  const db = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const request = db
      .transaction(WORKSPACES, 'readwrite')
      .objectStore(WORKSPACES)
      .put(normalized, workspaceKey(normalized.userId, normalized.repositoryId));
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
  await setActiveRepositoryId(normalized.userId, normalized.repositoryId);
}

export async function clearWorkspace(userId: string, repositoryId?: string): Promise<void> {
  const db = await openDatabase();
  if (repositoryId) {
    await new Promise<void>((resolve, reject) => {
      const request = db
        .transaction(WORKSPACES, 'readwrite')
        .objectStore(WORKSPACES)
        .delete(workspaceKey(userId, repositoryId));
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
    const active = await loadActiveRepositoryId(userId);
    if (active === repositoryId) {
      const remaining = await listWorkspaces(userId);
      if (remaining[0]) await setActiveRepositoryId(userId, remaining[0].repositoryId);
      else {
        await new Promise<void>((resolve, reject) => {
          const request = db
            .transaction(META, 'readwrite')
            .objectStore(META)
            .delete(`active:${userId}`);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      }
    }
    return;
  }
  const all = await listWorkspaces(userId);
  await Promise.all(all.map((row) => clearWorkspace(userId, row.repositoryId)));
}

export class IndexedDbRepositoryStore implements RepositoryStore {
  constructor(
    private userId: string,
    private repositoryId?: string,
  ) {}
  async load(): Promise<RepositorySnapshot | null> {
    return (await loadWorkspace(this.userId, this.repositoryId))?.snapshot ?? null;
  }
  async save(snapshot: RepositorySnapshot): Promise<void> {
    const workspace = await loadWorkspace(this.userId, this.repositoryId);
    if (!workspace) throw new Error('Workspace has not been initialized');
    await saveWorkspace({ ...workspace, snapshot });
  }
  async clear(): Promise<void> {
    await clearWorkspace(this.userId, this.repositoryId);
  }
}

export const serializeDeviceKeys = (keys: DeviceKeys): StoredWorkspace['deviceKeys'] => ({
  encryptionPrivateKey: [...keys.encryptionPrivateKey],
  encryptionPublicKey: [...keys.encryptionPublicKey],
  signingPrivateKey: [...keys.signingPrivateKey],
  signingPublicKey: [...keys.signingPublicKey],
});

export const deserializeDeviceKeys = (keys: StoredWorkspace['deviceKeys']): DeviceKeys => ({
  encryptionPrivateKey: Uint8Array.from(keys.encryptionPrivateKey),
  encryptionPublicKey: Uint8Array.from(keys.encryptionPublicKey),
  signingPrivateKey: Uint8Array.from(keys.signingPrivateKey),
  signingPublicKey: Uint8Array.from(keys.signingPublicKey),
});
